/* eslint-disable react-hooks/exhaustive-deps */
/**
 * LM Mobile · Daily Fleet Status
 * One-tap update of each truck's current Driver / Party / Location / Status.
 * Generates a WhatsApp-shareable report for management.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import {
  Truck,
  Pencil,
  Share2,
  RefreshCw,
  Check,
  Search,
  X,
  Copy as CopyIcon,
  Printer,
} from "lucide-react";

const STATUS_OPTIONS = [
  "on loading",
  "loaded",
  "on the way",
  "delivered",
  "on unloading",
  "return",
  "idle",
  "in workshop",
  "breakdown",
];

export default function FleetStatusBoard() {
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);    // truck row being edited
  const [share, setShare] = useState(false);
  const location = useLocation();
  // When mounted inside LM Shell (path starts /lm), leave room for the
  // bottom-nav so the sticky CTA doesn't overlap the tab bar.
  const inLMShell = location.pathname.startsWith("/lm");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: trucks }, { data: drvs }] = await Promise.all([
        api.get("/logistics/fleet-status?active_only=true"),
        api.get("/logistics/fleet-status/eligible-drivers"),
      ]);
      setRows(trucks || []);
      setDrivers(drvs || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.truck_no || "").toLowerCase().includes(q) ||
        (r.current_driver_name_resolved || "").toLowerCase().includes(q) ||
        (r.current_party || "").toLowerCase().includes(q) ||
        (r.current_location || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <div className="space-y-3 pb-24" data-testid="fleet-status-board">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            LM · Daily Fleet Status
          </div>
          <h1
            className="text-xl font-bold text-slate-900"
            style={{ fontFamily: "Chivo" }}
          >
            {rows.length} Trucks
          </h1>
        </div>
        <button
          onClick={load}
          className="rounded-lg ring-1 ring-slate-200 bg-white p-2"
          data-testid="fleet-refresh"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4 text-slate-600" />
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search truck / driver / party / location…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg ring-1 ring-slate-200"
          data-testid="fleet-search"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-10">
          No trucks match.
        </div>
      ) : (
        <div className="rounded-xl bg-white ring-1 ring-slate-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-[10px] uppercase text-slate-500">
                <th className="text-left px-2 py-1.5">Vehicle</th>
                <th className="text-left px-2 py-1.5">Driver</th>
                <th className="text-left px-2 py-1.5">Party</th>
                <th className="text-left px-2 py-1.5">Location</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-semibold whitespace-nowrap">
                    {r.truck_no}
                  </td>
                  <td className="px-2 py-1.5">
                    {r.current_driver_name_resolved || (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {r.current_party || (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {r.current_location || (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      onClick={() => setEditing(r)}
                      data-testid={`fleet-edit-${r.id}`}
                      className="text-bravura-700 p-1 hover:bg-slate-100 rounded"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bottom sticky CTA — leaves room for LM Shell's bottom-nav when applicable. */}
      <div className={`fixed inset-x-0 bg-white border-t border-slate-200 p-3 z-40 ${
        inLMShell ? "bottom-16" : "bottom-0"
      }`}>
        <button
          onClick={() => setShare(true)}
          data-testid="fleet-share-btn"
          className="w-full max-w-md mx-auto block rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white py-3 font-bold flex items-center justify-center gap-2"
        >
          <Share2 className="h-4 w-4" /> Share with Management
        </button>
      </div>

      {/* Edit modal */}
      {editing && (
        <EditModal
          truck={editing}
          drivers={drivers}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {/* Share modal */}
      {share && (
        <ShareModal rows={rows} onClose={() => setShare(false)} />
      )}
    </div>
  );
}

function EditModal({ truck, drivers, onClose, onSaved }) {
  const [driverId, setDriverId] = useState(truck.current_driver_id || "");
  const [driverName, setDriverName] = useState(
    truck.current_driver_name || "",
  );
  const [party, setParty] = useState(truck.current_party || "");
  const [location, setLocation] = useState(truck.current_location || "");
  const [status, setStatus] = useState(truck.current_status || "");
  const [remarks, setRemarks] = useState(truck.remarks || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        current_driver_id: driverId || null,
        current_driver_name: driverName || null,
        current_party: party || null,
        current_location: location || null,
        current_status: status || null,
        remarks: remarks || null,
      };
      await api.patch(
        `/logistics/trucks/${truck.id}/fleet-status`,
        payload,
      );
      toast.success("Updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Update Fleet Status
            </div>
            <h2 className="text-lg font-bold">{truck.truck_no}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <Field label="Driver">
          <select
            value={driverId}
            onChange={(e) => {
              setDriverId(e.target.value);
              const d = drivers.find((x) => x.id === e.target.value);
              if (d) setDriverName(d.name);
            }}
            className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
            data-testid="fleet-edit-driver"
          >
            <option value="">— Select driver —</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} {d.employee_code ? `(${d.employee_code})` : ""}
              </option>
            ))}
          </select>
          <input
            value={driverName}
            onChange={(e) => setDriverName(e.target.value)}
            placeholder="Or type driver name (free text)"
            className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-2 py-2 text-xs"
          />
        </Field>

        <Field label="Party">
          <input
            value={party}
            onChange={(e) => setParty(e.target.value)}
            placeholder="e.g. Devgiri / moldmaan / on loading"
            className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
            data-testid="fleet-edit-party"
          />
        </Field>

        <Field label="Location">
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Sitapura / Sonipat / on the way"
            className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
            data-testid="fleet-edit-location"
          />
        </Field>

        <Field label="Status">
          <div className="grid grid-cols-3 gap-1.5">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`text-[11px] py-1.5 rounded-lg ring-1 ${
                  status === s
                    ? "bg-bravura text-white ring-bravura"
                    : "bg-white text-slate-700 ring-slate-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Remarks (optional)">
          <input
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
          />
        </Field>

        <button
          disabled={busy}
          onClick={save}
          data-testid="fleet-edit-save"
          className="w-full rounded-xl bg-slate-900 text-white py-3 font-bold disabled:bg-slate-400 flex items-center justify-center gap-2"
        >
          <Check className="h-4 w-4" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function ShareModal({ rows, onClose }) {
  // Build pipe-formatted text + table
  const today = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const lines = [
    `🚛 *Bravura Trucking — Fleet Status*`,
    `📅 ${today}`,
    ``,
    "```",
    "Vehicle      | Driver         | Party       | Location",
    "------------ | -------------- | ----------- | ------------",
  ];
  rows.forEach((r) => {
    const v = (r.truck_no || "").padEnd(12).slice(0, 12);
    const d = (r.current_driver_name_resolved || "—").padEnd(14).slice(0, 14);
    const p = (r.current_party || "—").padEnd(11).slice(0, 11);
    const l = (r.current_location || "—").padEnd(12).slice(0, 12);
    lines.push(`${v} | ${d} | ${p} | ${l}`);
  });
  lines.push("```");
  const text = lines.join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const sendWA = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const printPdf = () => {
    window.print();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="fleet-share-modal"
    >
      <div
        className="bg-white rounded-2xl p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Share Fleet Status</h2>
          <button onClick={onClose} className="text-slate-500 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Preview table (printable) */}
        <div className="border rounded-lg overflow-hidden print:border-2 print:border-black">
          <div className="bg-slate-900 text-white px-3 py-2 text-sm font-bold print:bg-white print:text-black">
            Bravura Trucking — Fleet Status · {today}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr className="text-[10px] uppercase text-slate-700">
                <th className="text-left px-2 py-1.5 border border-slate-200">
                  Vehicle
                </th>
                <th className="text-left px-2 py-1.5 border border-slate-200">
                  Driver
                </th>
                <th className="text-left px-2 py-1.5 border border-slate-200">
                  Party
                </th>
                <th className="text-left px-2 py-1.5 border border-slate-200">
                  Location
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-2 py-1.5 font-semibold border border-slate-200">
                    {r.truck_no}
                  </td>
                  <td className="px-2 py-1.5 border border-slate-200">
                    {r.current_driver_name_resolved || ""}
                  </td>
                  <td className="px-2 py-1.5 border border-slate-200">
                    {r.current_party || ""}
                  </td>
                  <td className="px-2 py-1.5 border border-slate-200">
                    {r.current_location || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* WhatsApp text preview */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
            WhatsApp text preview
          </div>
          <pre className="text-[10px] bg-slate-50 p-2 rounded-lg max-h-40 overflow-y-auto whitespace-pre">
            {text}
          </pre>
        </div>

        <div className="grid grid-cols-3 gap-2 print:hidden">
          <button
            onClick={sendWA}
            data-testid="fleet-share-whatsapp"
            className="rounded-xl bg-emerald-600 text-white py-3 font-bold text-sm flex items-center justify-center gap-1.5"
          >
            <Share2 className="h-4 w-4" /> WhatsApp
          </button>
          <button
            onClick={copy}
            data-testid="fleet-share-copy"
            className="rounded-xl bg-slate-900 text-white py-3 font-bold text-sm flex items-center justify-center gap-1.5"
          >
            <CopyIcon className="h-4 w-4" /> Copy
          </button>
          <button
            onClick={printPdf}
            data-testid="fleet-share-print"
            className="rounded-xl bg-sky-600 text-white py-3 font-bold text-sm flex items-center justify-center gap-1.5"
          >
            <Printer className="h-4 w-4" /> Print/PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}
